import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Component to handle map clicks
function MapClickHandler({ onLocationSelect, disabled }) {
  useMapEvents({
    click(e) {
      if (!disabled) {
        onLocationSelect(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
}

// Component to recenter map when coordinates change
function MapRecenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) {
      map.setView([lat, lng], 13);
    }
  }, [lat, lng, map]);
  return null;
}

// Component to fix map size when container becomes visible
function MapSizeFixer() {
  const map = useMap();
  useEffect(() => {
    // Invalidate size after a short delay to ensure container is fully rendered
    const timeout = setTimeout(() => {
      map.invalidateSize();
    }, 100);
    return () => clearTimeout(timeout);
  }, [map]);
  return null;
}

/**
 * CoordinatePicker - Map picker for selecting GPS coordinates
 *
 * @param {number|null} latitude - Current latitude value
 * @param {number|null} longitude - Current longitude value
 * @param {function} onChange - Callback: (lat, lng) => void
 * @param {boolean} disabled - Disable editing
 */
export default function CoordinatePicker({ latitude, longitude, onChange, disabled = false }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tempLat, setTempLat] = useState(latitude ?? '');
  const [tempLng, setTempLng] = useState(longitude ?? '');
  const mapRef = useRef(null);

  // Default center: Modena, Italy (CAI Modena headquarters)
  const defaultCenter = [44.6471, 10.9252];
  const center = (latitude && longitude) ? [latitude, longitude] : defaultCenter;

  useEffect(() => {
    setTempLat(latitude ?? '');
    setTempLng(longitude ?? '');
  }, [latitude, longitude]);

  // Auto-expand if coordinates are set
  useEffect(() => {
    if (latitude && longitude && !isExpanded) {
      setIsExpanded(true);
    }
  }, [latitude, longitude, isExpanded]);

  const handleMapClick = (lat, lng) => {
    if (disabled) return;
    const roundedLat = Math.round(lat * 1000000) / 1000000;
    const roundedLng = Math.round(lng * 1000000) / 1000000;
    onChange(roundedLat, roundedLng);
  };

  const handleManualChange = () => {
    const lat = parseFloat(tempLat);
    const lng = parseFloat(tempLng);

    if (tempLat === '' && tempLng === '') {
      onChange(null, null);
      return;
    }

    if (isNaN(lat) || isNaN(lng)) return;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;

    onChange(lat, lng);
  };

  const handleClear = () => {
    onChange(null, null);
    setTempLat('');
    setTempLng('');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
        >
          <span className="text-sm">{isExpanded ? '▼' : '▶'}</span>
          <span>Coordinate GPS (opzionale)</span>
        </button>
        {latitude && longitude && (
          <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
            {latitude.toFixed(4)}, {longitude.toFixed(4)}
          </span>
        )}
      </div>

      {isExpanded && (
        <div className="space-y-3 p-3 bg-gray-50 rounded border border-gray-200">
          <p className="text-xs text-gray-600">
            Seleziona un punto sulla mappa o inserisci le coordinate manualmente. Le coordinate verranno utilizzate per mostrare le previsioni meteo.
          </p>

          {/* Manual input fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Latitudine
              </label>
              <input
                type="number"
                step="0.000001"
                min="-90"
                max="90"
                value={tempLat}
                onChange={(e) => setTempLat(e.target.value)}
                onBlur={handleManualChange}
                onKeyDown={(e) => e.key === 'Enter' && handleManualChange()}
                disabled={disabled}
                placeholder="es. 44.6471"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Longitudine
              </label>
              <input
                type="number"
                step="0.000001"
                min="-180"
                max="180"
                value={tempLng}
                onChange={(e) => setTempLng(e.target.value)}
                onBlur={handleManualChange}
                onKeyDown={(e) => e.key === 'Enter' && handleManualChange()}
                disabled={disabled}
                placeholder="es. 10.9252"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              />
            </div>
          </div>

          {/* Map */}
          <div className="h-64 rounded overflow-hidden border border-gray-300">
            <MapContainer
              center={center}
              zoom={latitude && longitude ? 13 : 8}
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              />
              <MapSizeFixer />
              {!disabled && <MapClickHandler onLocationSelect={handleMapClick} disabled={disabled} />}
              {latitude && longitude && (
                <>
                  <Marker position={[latitude, longitude]} />
                  <MapRecenter lat={latitude} lng={longitude} />
                </>
              )}
            </MapContainer>
          </div>

          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-500">
              Clicca sulla mappa per selezionare il punto di partenza della gita.
            </p>
            {latitude && longitude && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-red-600 hover:text-red-800 font-medium"
              >
                Rimuovi coordinate
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
